import React, { useRef, useLayoutEffect } from 'react';
import { Container, Row, Col, Card, colors } from '@everlywell/leaves';
import { useSprings } from 'react-spring';
import useComponentSize from '@rehooks/component-size';
import { useDrag } from 'react-use-gesture';
import { CTA } from '../../../utils/types';
import { clamp } from 'lodash-es';

import SectionWrapper from 'components/SectionWrapper';
import * as S from './styles';
import { generateKey } from 'utils/helpers';
import { GatsbyImage as Img, IGatsbyImageData } from 'gatsby-plugin-image';

type ContentWithImage = {
  heading: string;
  description: string;
  image: IGatsbyImageData;
  imageAlt: string;
  cta: CTA;
};

export interface RecommendedArticlesSectionProps {
  title: string;
  headline: string;
  mainCta?: CTA;
  listOfContent: ContentWithImage[];
}

export const RecommendedArticlesSection: React.FC<RecommendedArticlesSectionProps> =
  ({ title, headline, mainCta, listOfContent }) => {
    const index = useRef<number>(0);
    const innerRef = useRef<HTMLDivElement>(null);
    const size = useComponentSize(innerRef);

    const [props, set] = useSprings(listOfContent.length, (i) => {
      return {
        x: i * window.innerWidth,
        display: 'flex',
      };
    });

    // useDrag will bind to the component and captures data bout gesture movement
    const bind = useDrag(
      ({ down, movement: [mx], direction: [xDir], distance, cancel }) => {
        // if user swipes more than 1/3 to either side
        if (down && distance > window.innerWidth / 3) {
          // find the next slide index
          const nextSlideIndex = clamp(
            index.current + (xDir > 0 ? -1 : 1),
            0,
            listOfContent.length - 1,
          );
          // set new slide index
          index.current = nextSlideIndex;
          if (cancel) {
            cancel();
          }
        }

        // update position of springs based on gesture movement
        set((i) => {
          if (i < index.current - 1 || i > index.current + 1) {
            return { display: 'none' };
          }
          const x = (i - index.current) * size.width + (down ? mx : 0);
          return { x, display: 'flex' };
        });
      },
    );

    // use width of container to offset
    useLayoutEffect(() => {
      if (size.width) {
        set((i) => {
          return { x: i * size.width };
        });
      }
    }, [size.width]);

    return (
      <SectionWrapper style={{ backgroundColor: colors.teal1 }}>
        <Container>
          <Row>
            <Col xs={12}>
              <S.Title>{title}</S.Title>
              <S.Headline>{headline}</S.Headline>
            </Col>
          </Row>
        </Container>
        <S.MobileCardsContainer>
          <S.MobileCardsInner ref={innerRef}>
            {props.map(({ x, display }, i) => (
              <S.AnimatedCardWrapper
                {...bind()}
                key={generateKey()}
                style={{ display, x }}
              >
                <Card.Article
                  image={
                    <Img
                      image={listOfContent[i].image}
                      style={{ width: '100%', height: '100%' }}
                      alt={listOfContent[i].imageAlt}
                    />
                  }
                  imageAlt={`slide-${listOfContent[i].imageAlt}`}
                  title={listOfContent[i].heading}
                  copy={listOfContent[i].description}
                  url={listOfContent[i].cta.url}
                  linkText={listOfContent[i].cta.text}
                />
              </S.AnimatedCardWrapper>
            ))}
          </S.MobileCardsInner>
        </S.MobileCardsContainer>
        <S.DesktopCardsContainer>
          <Row>
            <S.Col md={12} lg={12}>
              {listOfContent.map((item) => (
                <S.CardWrapper key={generateKey()}>
                  <Card.Article
                    image={
                      <Img
                        image={item.image}
                        style={{ width: '100%', height: '100%' }}
                        alt={item.imageAlt}
                      />
                    }
                    imageAlt={`slide-${item.imageAlt}`}
                    title={item.heading}
                    copy={item.description}
                    url={item.cta.url}
                    linkText={item.cta.text}
                  />
                </S.CardWrapper>
              ))}
            </S.Col>
          </Row>
        </S.DesktopCardsContainer>
        {mainCta ? (
          <S.CtaContainer>
            <S.Button appearance="secondary" href={mainCta.url}>
              {mainCta.text}
            </S.Button>
          </S.CtaContainer>
        ) : null}
      </SectionWrapper>
    );
  };

export default RecommendedArticlesSection;
